import * as React from "react";
import './Pagination.scss';

interface PaginationClientProps {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    pageSizeOptions?: number[];
}

const pageSizeCollection = [5, 10, 20, 30, 40, 50, 100];

const PaginationClient: React.FC<PaginationClientProps> = ({
    currentPage,
    totalPages,
    pageSize,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = pageSizeCollection,
}) => {
    const maxPagesToShow = 4;
    const blockSize = totalPages < maxPagesToShow ? totalPages : maxPagesToShow;
    const currentBlock = Math.ceil(currentPage / blockSize) - 1;

    const startPage = currentBlock * blockSize + 1;
    const endPage = Math.min(totalPages, startPage + blockSize - 1);
    const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

    const showLeftDots = currentBlock > 0;
    const showRightDots = currentBlock < Math.ceil(totalPages / blockSize) - 1;

    const handleLeftDotsClick = () => {
        const newPage = currentPage - (currentPage % blockSize === 0 ? blockSize : currentPage % blockSize);
        onPageChange(newPage);
    };

    const handleRightDotsClick = () => {
        const newPage = currentPage + (blockSize - (currentPage % blockSize === 0 ? blockSize : currentPage % blockSize)) + 1;
        onPageChange(Math.min(newPage, totalPages));
    };

    return (
        <nav className="pagination-container">
            <ul className="pagination">
                <li className={`page-item${currentPage === 1 ? ' disabled' : ''}`}>
                    <button className="page-link" onClick={() => onPageChange(1)} disabled={currentPage === 1} style={{ outline: 'none', boxShadow: 'none' }}>
                        <i className="icon angle-right-back fa-lg" aria-hidden="true" />
                    </button>
                </li>
                <li className={`page-item${currentPage === 1 ? ' disabled' : ''}`}>
                    <button className="page-link" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={{ outline: 'none', boxShadow: 'none' }}>
                        <i className="icon angle-left fa-lg" aria-hidden="true" />
                    </button>
                </li>
                {showLeftDots && (
                    <li className="page-item">
                        <button className="page-link" onClick={handleLeftDotsClick} style={{ outline: 'none', boxShadow: 'none' }}>...</button>
                    </li>
                )}
                {pages.map((page) => (
                    <li key={page} className={`page-item${currentPage === page ? ' active' : ''}`}>
                        <button className="page-link" onClick={() => onPageChange(page)} style={{ outline: 'none', boxShadow: 'none' }}>
                            <strong>{page}</strong>
                        </button>
                    </li>
                ))}
                {showRightDots && (
                    <li className="page-item">
                        <button className="page-link" onClick={handleRightDotsClick} style={{ outline: 'none', boxShadow: 'none' }}>...</button>
                    </li>
                )}
                <li className={`page-item${currentPage === totalPages || totalPages === 0 ? ' disabled' : ''}`}>
                    <button className="page-link" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} style={{ outline: 'none', boxShadow: 'none' }}>
                        <i className="icon angle-right fa-lg" aria-hidden="true" />
                    </button>
                </li>
                <li className={`page-item${currentPage === totalPages || totalPages === 0 ? ' disabled' : ''}`}>
                    <button className="page-link" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages || totalPages === 0} style={{ outline: 'none', boxShadow: 'none' }}>
                        <i className="icon angle-right-next fa-lg" aria-hidden="true" />
                    </button>
                </li>
            </ul>
            <div className="page-size">
                <select className="form-control" value={pageSize} onChange={e => onPageSizeChange(Number(e.target.value))}>
                    {pageSizeOptions.map((size, ind) => (
                        <option key={ind} value={size}>{size}</option>
                    ))}
                </select>
                <div className="item-per-page">Items per page</div>
            </div>
        </nav>
    );
};

export default PaginationClient;
